import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../utils/classname';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface ITooltipProps extends PropsWithChildren {
    content: ReactNode;
    position?: TooltipPosition;

    bgColor?: CSSProperties['backgroundColor'];
    textColor?: CSSProperties['color'];
    borderRadius?: CSSProperties['borderRadius'];
    borderColor?: CSSProperties['borderColor'];
    borderWidth?: CSSProperties['borderWidth'];

    padding?: CSSProperties['padding'];
    margin?: CSSProperties['margin'];
    gap?: CSSProperties['gap'];

    fontSize?: CSSProperties['fontSize'];
    fontWeight?: CSSProperties['fontWeight'];

    width?: CSSProperties['width'];
    height?: CSSProperties['height'];
    maxWidth?: CSSProperties['maxWidth'];

    className?: string;
    tooltipClassName?: string;
    style?: CSSProperties;
    tooltipStyle?: CSSProperties;
}

export default function Tooltip({
    content,
    position = 'top',
    bgColor = 'var(--color-tooltip-bg, #1a1a1a)',
    textColor = '#ffffff',
    borderRadius = '6px',
    borderColor,
    borderWidth,
    padding = '0.4rem 0.6rem',
    margin,
    gap,
    fontSize = '1rem',
    fontWeight,
    width,
    height,
    maxWidth = '240px',
    className,
    tooltipClassName,
    style,
    tooltipStyle,
    children,
}: ITooltipProps) {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const tooltip = tooltipRef.current;
        if (!trigger || !tooltip) return;

        const triggerRect = trigger.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        let left = triggerRect.left;
        let top = triggerRect.top;

        switch (position) {
            case 'top':
                left += (triggerRect.width - tooltipRect.width) / 2;
                top -= tooltipRect.height + (gap ? Number(gap) : 8);
                break;
            case 'bottom':
                left += (triggerRect.width - tooltipRect.width) / 2;
                top += triggerRect.height + (gap ? Number(gap) : 8);
                break;
            case 'left':
                left -= tooltipRect.width + (gap ? Number(gap) : 8);
                top += (triggerRect.height - tooltipRect.height) / 2;
                break;
            case 'right':
                left += triggerRect.width + (gap ? Number(gap) : 8);
                top += (triggerRect.height - tooltipRect.height) / 2;
                break;
        }

        setCoords({ left: Math.round(left), top: Math.round(top) });
    }, [position, gap]);

    useEffect(() => {
        if (!visible) return;
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [visible, updatePosition]);

    const tooltipBodyStyle: CSSProperties = {
        backgroundColor: bgColor,
        color: textColor,
        borderRadius,
        borderColor,
        borderWidth,
        borderStyle: borderWidth ? 'solid' : undefined,
        padding,
        margin,
        fontSize,
        fontWeight,
        width,
        height,
        maxWidth,
        whiteSpace: 'normal',
        ...tooltipStyle,
    };

    const arrowStyle: CSSProperties = {
        position: 'absolute',
        width: '8px',
        height: '8px',
        backgroundColor: bgColor,
        borderColor,
        borderWidth,
        borderStyle: borderWidth ? 'solid' : undefined,
    };

    const arrowOffsets: Record<TooltipPosition, CSSProperties> = {
        top: { bottom: '-4px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' },
        bottom: { top: '-4px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' },
        left: { right: '-4px', top: '50%', transform: 'translateY(-50%) rotate(45deg)' },
        right: { left: '-4px', top: '50%', transform: 'translateY(-50%) rotate(45deg)' },
    };

    return (
        <div
            ref={triggerRef}
            className={cn('xv-tooltip-wrapper', className)}
            style={{ position: 'relative', display: 'inline-flex', ...style }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
        >
            {children}
            {visible &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={cn('xv-tooltip', tooltipClassName)}
                        style={{
                            position: 'fixed',
                            left: coords.left,
                            top: coords.top,
                            zIndex: 9999,
                            pointerEvents: 'none',
                        }}
                        role="tooltip"
                    >
                        <div style={tooltipBodyStyle}>{content}</div>
                        <div style={{ ...arrowStyle, ...arrowOffsets[position] }} />
                    </div>,
                    document.body
                )}
        </div>
    );
}
